import { useEffect, useRef, useState } from 'react';
import useApi from './useApi';
import wait from '../helpers/wait';
import { segmentEvent } from '../helpers/sendEvent';

const MAX_ATTEMPTS = 10;
const RETRY_DELAY_MS = 2000;
const VIEW_CARD_DELAY_MS = 10000;
const HIDE_COPY_BUTTON = '<style>#buttonClipboard{display:none!important}</style>';

const withRetries = async (request, attempt = 1) => {
  try {
    return await request();
  } catch (error) {
    if (attempt >= MAX_ATTEMPTS) {
      throw error;
    }
    await wait(RETRY_DELAY_MS * 2 ** (attempt - 1));
    return withRetries(request, attempt + 1);
  }
};

export default function useVirtualCard() {
  const { request } = useApi();
  const cardFrameRef = useRef(null);
  const isMountedRef = useRef(true);
  const [cardReady, setCardReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const loadVirtualCard = async (uid, instoreToken) => {
    try {
      const jwt = await withRetries(() => request.vccSyncRequest(uid, instoreToken));
      await wait(VIEW_CARD_DELAY_MS);
      const html = await withRetries(() => request.viewCardRequest(jwt));
      if (!isMountedRef.current) {
        return;
      }
      if (!cardFrameRef.current) {
        throw new Error('virtual card frame is not mounted');
      }
      cardFrameRef.current.srcdoc = `${html}${HIDE_COPY_BUTTON}`;
      setCardReady(true);
      // Only the application uid: the card number, CVV and tokens must never reach analytics.
      segmentEvent('track', 'Virtual Card Generated', { application_id: uid });
    } catch (e) {
      if (isMountedRef.current) {
        setError(e);
      }
    }
  };

  return { cardFrameRef, cardReady, error, loadVirtualCard };
}
